import {
  DropdownButton,
  DropdownButtonProps,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import type {
  BasicFilterControl,
  ContextControl,
  DashboardFilterValue,
  InstrumentPanel,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import { PropsWithChildren } from 'lib-shared-types-utils';
import remove from 'lodash/remove';
import {
  ElementRef,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';

import { isOptionalOperator } from '../../utils/filters/isOptionalOperator';

import { AnalyticsFilterChangeCallback } from './analyticsFilter.types';
import { AnalyticsFilterItem } from './components/item/analyticsFilterItem';
import { AnalyticsFilterLayout } from './components/layout/analyticsFilterLayout';
import { AnalyticsFilterOptions } from './components/options/analyticsFilterOptions';
import { AnalyticsFilterSearch } from './components/search/analyticsFilterSearch';
import { AnalyticsFilterSelectAll } from './components/selectAll/analyticsFilterSelectAll';

/**
 * The number of filter values to render initially and incrementally while
 * scrolling, to avoid performance issues when receiving large lists of values.
 */
const RENDER_BATCH_SIZE = 100;

type AnalyticsFilterProps<
  TControl extends ContextControl | BasicFilterControl,
> = {
  /** The parent instrument panel, if relevant. */
  panel?: InstrumentPanel;
  /** The filter field. */
  field: TControl['field'];
  /** The values available to select. */
  availableValues: readonly DashboardFilterValue[];
  /** The values that are currently selected. */
  selectedValues?: TControl['values'];
  /** The values to display at the top of the list. */
  pinnedValues?: TControl['values'];
  /* The values that are not available to select. */
  disabledValues?: TControl['values'];
  /** The operators available to select. */
  availableOperators?: readonly TControl['operator'][];
  /** The default selected operator. */
  defaultOperator?: TControl['operator'];
  /** The currently selected operator. */
  selectedOperator?: TControl['operator'];
  /** Whether the filter supports multiple value selection. */
  isMultiple: boolean;
  /** Whether the filter values can be cleared. */
  isClearable?: boolean;
  /** Whether selecting filter values is disabled. */
  isSelectionDisabled?: boolean;
  /** Whether the filter supports selecting all values at once. */
  hasDeterminateSelection?: boolean;
  /** The props to pass down to the Button component. */
  buttonProps?: DropdownButtonProps['buttonProps'];
  /** A custom element rendered above the selection list. */
  customHeaderElement?: JSX.Element;
  /** Callback when the filter is changed. */
  onChange: AnalyticsFilterChangeCallback<TControl>;
  /** Callback when the dropdown is toggled. */
  onToggle?: (isOpen: boolean) => void;
} & PropsWithChildren<string>;

const AnalyticsFilter = <TControl extends ContextControl | BasicFilterControl>({
  children,
  panel,
  field,
  availableValues,
  selectedValues = [],
  pinnedValues = [],
  disabledValues = [],
  availableOperators = [],
  defaultOperator = availableOperators?.[0] ?? 'is_one',
  selectedOperator = defaultOperator,
  isMultiple = true,
  isClearable = true,
  isSelectionDisabled = false,
  hasDeterminateSelection = isMultiple,
  buttonProps = {},
  customHeaderElement,
  onChange,
  onToggle,
}: AnalyticsFilterProps<TControl>): JSX.Element => {
  const { dashboard } = useAnalyticsState(panel);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [renderedCount, setRenderedCount] = useState(RENDER_BATCH_SIZE);

  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const searchRef = useRef<ElementRef<typeof AnalyticsFilterSearch>>(null);
  const listWrapperRef = useRef<HTMLDivElement>(null);

  /**
   * The filter values visible to the user.
   *
   * These are first filtered according to the search criteria if any, and
   * finally ordered so that the values that should be pinned are shown first.
   */
  const visibleValues = useMemo((): readonly DashboardFilterValue[] => {
    const fuse = new Fuse(availableValues, {
      keys: ['label', 'value'],
      threshold: 0.2,
      distance: 200,
    });

    // The filtered values. Using spread operator to allow mutating the array below where we need to
    // pull out the pinned values.
    const values = [
      ...(debouncedSearchTerm
        ? fuse
            .search(debouncedSearchTerm)
            .map(({ item }) => item)
            // Prioritize active values over deactivated ones, by always sorting deactivated values
            // at the bottom, regardless of the score-based sorting that Fuse.js does.
            .sort((a, b) =>
              a.deactivated === b.deactivated ? 0 : a.deactivated ? 1 : -1,
            )
        : availableValues),
    ];

    // Extracts the pinned values from the visible values in order to render the at the top.
    const pinned: DashboardFilterValue[] = remove(
      values,
      ({ value, deactivated }) =>
        pinnedValues.some(
          ({ value: pinnedValue }) => pinnedValue === value && !deactivated,
        ),
    );

    return [...pinned, ...values];
  }, [availableValues, pinnedValues, debouncedSearchTerm]);

  useEffect(() => {
    setRenderedCount(RENDER_BATCH_SIZE);
  }, [debouncedSearchTerm]);

  useUpdateEffect(() => {
    onToggle?.(isOpen);
  }, [isOpen]);

  const handleToggleDropdown = useCallback(
    (newIsOpen: boolean) => {
      setIsOpen(newIsOpen);

      if (newIsOpen) {
        setRenderedCount(RENDER_BATCH_SIZE);

        ampli.analyticsFilterOpened({
          type: field,
          dashboard: dashboard.title,
          panel: panel?.title,
        });

        if (listWrapperRef.current) {
          listWrapperRef.current.scrollTop = 0;
        }
      }
    },
    [dashboard.title, field, panel?.title],
  );

  /**
   * Handles changing the filter values.
   *
   * If there is at least one value selected, the filter is either added or
   * updated in the state. Otherwise if no values remain selected, the filter is
   * removed from the state.
   */
  const handleChange = useCallback(
    ({
      selectedValues,
      selectedItem,
    }: SelectionListChangeEvent<TControl['values'][number]>) => {
      onChange({
        op: selectedValues.length ? 'upsert' : 'remove',
        filter: {
          field,
          values: selectedValues,
          operator: selectedOperator,
        } as unknown as TControl,
      });

      searchRef.current?.focus();

      // Close the dropdown automatically when selecting a single value.
      if (!isMultiple) {
        setIsOpen(false);
      }

      if (selectedItem.isSelected) {
        ampli.analyticsFilterValueAdded({
          dashboard: dashboard.title,
          panel: panel?.title,
          type: field,
          value: `${selectedItem.value.value}`,
        });
      }
    },
    [
      dashboard.title,
      field,
      isMultiple,
      onChange,
      panel?.title,
      selectedOperator,
    ],
  );

  /**
   * Handles changing all filter values at once.
   *
   * If there is at least one value selected, all are deselected and the filter
   * is removed from the state.
   */
  const handleChangeAll = useCallback(() => {
    const isDeselecting = selectedValues.length > 0;

    onChange({
      op: isDeselecting ? 'remove' : 'upsert',
      filter: {
        field,
        values: isDeselecting
          ? []
          : visibleValues.map(({ value }) => ({ value })),
        operator: selectedOperator,
      } as unknown as TControl,
    });

    ampli.analyticsFilterValueAdded({
      dashboard: dashboard.title,
      panel: panel?.title,
      type: field,
      value: isDeselecting ? 'deselect_all' : 'select_all',
    });
  }, [
    dashboard.title,
    field,
    onChange,
    panel?.title,
    selectedOperator,
    selectedValues.length,
    visibleValues,
  ]);

  /**
   * Handles changing the filter operator.
   *
   * If filter is restored to its default state, i.e. with the default operator
   * and no values selected, the filter is removed from the state.
   *
   * Otherwise if the filter operator is of the optional type, any previously
   * selected values are cleared and the filter is updated in the state.
   */
  const handleChangeOperator = useCallback(
    (operator: TControl['operator']) => {
      const isDefaultState =
        operator === availableOperators?.[0] && !selectedValues.length;

      onChange({
        op: isDefaultState ? 'remove' : 'upsert',
        filter: {
          field,
          values: isOptionalOperator(operator) ? [] : selectedValues,
          operator,
        } as unknown as TControl,
      });

      ampli.analyticsFilterOperatorUpdated({
        dashboard: dashboard.title,
        panel: panel?.title,
        type: field,
        operator,
      });
    },
    [
      availableOperators,
      dashboard.title,
      field,
      onChange,
      panel?.title,
      selectedValues,
    ],
  );

  /** Handles remove the filter from the state. */
  const handleClear = useCallback(() => {
    onChange({
      op: 'remove',
      filter: {
        field,
        values: [],
        operator: selectedOperator,
      } as unknown as TControl,
    });

    ampli.analyticsFilterCleared({
      dashboard: dashboard.title,
      panel: panel?.title,
      type: field,
    });
  }, [dashboard.title, field, onChange, panel?.title, selectedOperator]);

  const isEmpty = !visibleValues.length;

  return (
    <DropdownButton
      show={isOpen}
      dropdown={
        <AnalyticsFilterLayout>
          <AnalyticsFilterLayout.Row>
            <AnalyticsFilterSearch
              ref={searchRef}
              value={searchTerm}
              isOpen={isOpen}
              isDisabled={isSelectionDisabled}
              onChange={setSearchTerm}
            />
          </AnalyticsFilterLayout.Row>

          {customHeaderElement && !isEmpty && (
            <AnalyticsFilterLayout.Row noPadding borderBottom>
              {customHeaderElement}
            </AnalyticsFilterLayout.Row>
          )}

          {isMultiple && hasDeterminateSelection && !isEmpty && (
            <AnalyticsFilterLayout.Row noPadding borderBottom>
              <AnalyticsFilterSelectAll
                selectedValues={selectedValues}
                visibleValues={visibleValues}
                isDisabled={isSelectionDisabled}
                onChange={handleChangeAll}
              />
            </AnalyticsFilterLayout.Row>
          )}

          <AnalyticsFilterLayout.Row ref={listWrapperRef} noPadding scrollable>
            <SelectionList
              isMultiple={isMultiple}
              isActive={isOpen}
              isDisabled={isSelectionDisabled}
              selectedValues={
                selectedValues as Array<TControl['values'][number]>
              }
              onChange={handleChange}
              equalityFn={(a, b) => a.value === b.value}
              defaultActiveItemIndex={0}
            >
              {visibleValues
                .slice(0, renderedCount)
                .map(({ value, label, color, deactivated }, index, list) => {
                  const isFirstDeactivatedItem = Boolean(
                    deactivated &&
                    (index === 0 || !list[index - 1].deactivated),
                  );

                  return (
                    <Fragment key={value}>
                      {isFirstDeactivatedItem && (
                        <SelectionList.Divider label="Deactivated" />
                      )}

                      <AnalyticsFilterItem
                        value={{ value }}
                        label={label}
                        highlight={debouncedSearchTerm}
                        color={color}
                        isDisabled={disabledValues.some(
                          (disabledItem) => disabledItem.value === value,
                        )}
                        textProps={{
                          color: deactivated ? 'tertiary' : undefined,
                        }}
                      />
                    </Fragment>
                  );
                })}

              {renderedCount < visibleValues.length && (
                <li>
                  <Waypoint
                    onEnter={() =>
                      setRenderedCount((c) => c + RENDER_BATCH_SIZE)
                    }
                  />
                </li>
              )}

              {isEmpty && <AnalyticsFilterLayout.Empty />}
            </SelectionList>
          </AnalyticsFilterLayout.Row>

          {!isEmpty && availableOperators.length > 0 && (
            <AnalyticsFilterLayout.Row borderTop slottable>
              <AnalyticsFilterOptions
                operators={availableOperators}
                defaultOperator={defaultOperator}
                selectedOperator={selectedOperator}
                isMissingSelectedValues={!selectedValues.length}
                isDropdownOpen={isOpen}
                onChangeOperator={handleChangeOperator}
                {...(isClearable && { onClear: handleClear })}
              />
            </AnalyticsFilterLayout.Row>
          )}
        </AnalyticsFilterLayout>
      }
      onToggle={handleToggleDropdown}
      buttonProps={buttonProps}
      data-analytics-filter={field}
      fixed
    >
      {children}
    </DropdownButton>
  );
};

const _AnalyticsFilter = memo(AnalyticsFilter) as typeof AnalyticsFilter;

export { _AnalyticsFilter as AnalyticsFilter };
